/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-general
 * @responsibility Render the SET-F04 General pane from backend-authoritative organization hooks.
 * @state-owner TanStack Query
 * @allowed-imports same-module hooks/components; shared translator model
 * @forbidden direct API client; hardcoded organization data; member-management UI
 * @public-api GeneralSettingsPage
 * @side-effects invokes authorized same-module commands and cache invalidation
 * @tests GeneralSettingsSurface.test.tsx
 */

import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import {
  useOrganizationGeneral,
  useUpdateOrganization,
  useUploadOrganizationLogo,
} from '../api/settings-general.queries';
import { GeneralSettingsSurface } from './GeneralSettingsSurface';

function errorCodeOf(error: unknown): string | null {
  return error && typeof error === 'object' && 'code' in error ? String(error.code) : null;
}

export function GeneralSettingsPage({ mobile }: { mobile: boolean }) {
  const t = useTranslator();
  const generalQuery = useOrganizationGeneral();
  const updateOrganization = useUpdateOrganization();
  const uploadLogo = useUploadOrganizationLogo();

  if (generalQuery.isPending) {
    return (
      <VispoLoading className="general-settings-pane__loading" label={t('正在載入組織設定')} />
    );
  }

  const general = generalQuery.data;

  if (!general || general.organizationId === null || general.name === null) {
    return (
      <div className="general-settings-pane__error" role="alert">
        <span>{t('更新失敗，請重試')}</span>
        <button type="button" onClick={() => void generalQuery.refetch()}>
          {t('重新整理')}
        </button>
      </div>
    );
  }

  const expectedRevision = general.revision ?? 1;
  const organizationName = general.name;

  return (
    <GeneralSettingsSurface
      canManage={general.canManage}
      errorCode={errorCodeOf(updateOrganization.error)}
      logoUploadErrorCode={errorCodeOf(uploadLogo.error)}
      logoUrl={general.logoUrl}
      mobile={mobile}
      name={organizationName}
      onClearLogo={() =>
        updateOrganization.mutate({ name: organizationName, clearLogo: true, expectedRevision })
      }
      onResetLogoUploadError={() => {
        if (uploadLogo.error) uploadLogo.reset();
      }}
      onRetry={() => {
        updateOrganization.reset();
        void generalQuery.refetch();
      }}
      onSave={(name) => updateOrganization.mutate({ name, expectedRevision })}
      onUploadLogo={(file) => uploadLogo.mutate({ file, expectedRevision })}
      saving={updateOrganization.isPending}
      uploadingLogo={uploadLogo.isPending}
    />
  );
}
